/** @fileoverview Renders a checklist block whose rows are saved per record and can be ticked. */

import { useState } from 'react';
import { jsonPointerSegment } from '../../lib/previewSelection';
import type { CSSProperties } from 'react';
import { LoaderCircle } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

import type {
  ComponentStateRow,
  FabrikChecklistItemAction,
  FabrikComponent,
  FabrikRecordKeyRefValue,
  StoredOwner,
  InputSnapshot,
} from '../../types/apps';
import { useComponentState } from './appRuntimeContext';
import { getActionSendRecordKeyRefs, useComponentStateStore } from './componentStateStore';
import {
  calendarDateOf,
  daysBetween,
  fieldsForRecordKey,
  formatDate,
  formatDateTime,
  textOf,
  todayCalendarDate,
} from './helpers';
import type { CalendarDate } from './helpers';
import DoneTick from './DoneTick';
import InlineAlert from './InlineAlert';
import StateBlock from './StateBlock';
import type { FieldValues, StateRendererProps } from './types';
import { useComponentStateRefresh, useComponentStateRows } from './useComponentStateRows';
import { appButtonLook } from './appButton';
import ActionConfirmDialog from './ActionConfirmDialog';
import { useRunAction } from './useRunAction';

/**
 * Builds the "who · when" line under a ticked row, or '' when the row is not ticked.
 * @param value saved value of the checklist row
 */
const checkedByLine = (value: ComponentStateRow['value']): string => {
  const { checked, checkedBy, checkedAt } = value;
  if (checked !== true) {
    return '';
  }
  return [textOf(checkedBy), formatDateTime(checkedAt)].filter(Boolean).join(' · ');
};

/**
 * Reads the name and email of a row's owner. An old row holds a plain string, which is an email.
 * @param owner saved owner of the checklist row
 */
const ownerParts = (owner: StoredOwner | undefined): { label: string; detail: string } => {
  const { name, email } =
    typeof owner === 'string' || !owner ? { name: '', email: owner ?? '' } : owner;
  const nameText = textOf(name);
  const emailText = textOf(email);
  return nameText ? { label: nameText, detail: emailText } : { label: emailText, detail: '' };
};

/**
 * Tells whether a row's due date has passed and the row is not yet checked.
 * @param value saved value of the checklist row
 * @param today today's date in the viewer's own time zone
 */
const isLateRow = (value: ComponentStateRow['value'], today: CalendarDate): boolean => {
  const { checked, dueDate } = value;
  if (checked === true || typeof dueDate !== 'string') {
    return false;
  }
  const dueDay = calendarDateOf(dueDate);
  if (!dueDay) {
    return false;
  }
  return daysBetween(today, dueDay) < 0;
};

type ItemActionButtonProps = {
  action: FabrikChecklistItemAction;
  componentId: string;
  recordKey: FabrikRecordKeyRefValue[];
  recordKeyRefs: FabrikRecordKeyRefValue[];
  rowKey: string;
  fields: FieldValues;
  screenId: string;
  inputs: InputSnapshot;
};

/**
 * Small row button that runs one item action for one checklist row, then reloads the components
 * the action writes to.
 * @param props the item action, the checklist id and record key, the row key, and the record fields
 */
const ItemActionButton = ({
  action,
  componentId,
  recordKey,
  recordKeyRefs,
  rowKey,
  fields,
  screenId,
  inputs,
}: ItemActionButtonProps) => {
  const { label, target } = action;
  const { canRun, isBusy, isDone, error, runAction, confirmation } = useRunAction();

  return (
    <div>
      <Button
        type="button"
        {...appButtonLook({ size: 'sm' })}
        disabled={!canRun || isBusy}
        aria-busy={isBusy || undefined}
        onClick={() =>
          runAction({
            actionId: target.actionId,
            screenId,
            fields,
            inputs,
            targets: [{ id: componentId, recordKey }],
            recordKeyRefs,
            item: { componentId, key: rowKey },
          })
        }
      >
        {isBusy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
        <DoneTick isShown={isDone} />
        {label}
      </Button>
      {error && <InlineAlert className="mt-1">{error}</InlineAlert>}
      <ActionConfirmDialog {...confirmation} />
    </div>
  );
};

/**
 * Shows the title and one checkbox per saved row; ticking saves the change and reloads the rows.
 * @param props the checklist component and the record fields of the screen
 */
export default function ChecklistRenderer({
  component,
  screenId,
  fields,
  inputs,
  screenComponents,
  selectionPath,
}: StateRendererProps<'checklist'> & { screenComponents: FabrikComponent[] }) {
  const { id: componentId, title } = component;
  const componentState = useComponentState();
  const { actions, blockedActionIds } = useComponentStateStore();
  /* Item actions the viewer may not run are left out, as if the row had none of them. */
  const itemActions = component.itemActions?.filter(
    ({ target }) => !blockedActionIds.has(target.actionId)
  );
  const { refreshComponents } = useComponentStateRefresh();
  const { isAvailable, isLoading, rows, error } = useComponentStateRows(
    componentId,
    fields,
    component.recordKey,
    screenId,
    inputs
  );
  const [pendingKey, setPendingKey] = useState('');
  const [tickError, setTickError] = useState('');

  /**
   * Saves the new checked state of one row.
   * @param key key of the row
   * @param checked the new checked state
   */
  const tickRow = async (key: string, checked: boolean) => {
    if (!componentState) {
      return;
    }
    setPendingKey(key);
    setTickError('');
    try {
      await componentState.tick({
        componentId,
        key,
        screenId,
        fields: fieldsForRecordKey(fields, component.recordKey),
        inputs,
        recordKey: component.recordKey,
        checked,
      });
      await refreshComponents([component], fields, screenId, inputs);
    } catch (reason) {
      setTickError(reason instanceof Error ? reason.message : 'Unable to save this change.');
    } finally {
      setPendingKey('');
    }
  };

  const today = todayCalendarDate();
  const doneCount = rows.filter(({ value }) => value.checked === true).length;
  /* "1 of 3 done" with a thin bar, so progress shows at a glance. */
  const progress = rows.length > 0 && (
    <div className="flex items-center gap-2">
      <span className="type-small text-muted-foreground tabular-nums">
        {doneCount} of {rows.length} done
      </span>
      <span className="h-1 w-16 overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <span
          className="block h-full w-(--checklist-progress) rounded-full bg-primary transition-all"
          style={{ '--checklist-progress': `${(doneCount / rows.length) * 100}%` } as CSSProperties}
        />
      </span>
    </div>
  );

  return (
    <StateBlock
      title={title}
      selectionPath={selectionPath}
      isAvailable={isAvailable}
      isLoading={isLoading}
      hasRows={rows.length > 0}
      error={error || tickError}
      emptyText="No tasks yet."
      titleAside={progress}
    >
      <ul className="divide-y divide-border overflow-hidden rounded-md border border-border bg-card">
        {rows.map((row) => {
          const { key, value } = row;
          const checkboxId = `checklist-${componentId}-${key}`;
          const rowPath = selectionPath
            ? `${selectionPath}/rows/${jsonPointerSegment(key)}`
            : undefined;
          const byLine = checkedByLine(value);
          const { label: ownerLabel, detail: ownerDetail } = ownerParts(value.owner);
          const ownerLine = ownerDetail ? `${ownerLabel} · ${ownerDetail}` : ownerLabel;
          const dueDateText = formatDate(value.dueDate);
          const late = isLateRow(value, today);
          return (
            <li
              data-fabrik-selection-path={rowPath}
              className="flex min-h-app-row flex-wrap items-start gap-x-3 gap-y-2 px-3 py-2.5 sm:flex-nowrap sm:items-center"
              key={key}
            >
              <Checkbox
                id={checkboxId}
                className="mt-0.5 bg-card sm:mt-0 dark:bg-card"
                checked={value.checked === true}
                disabled={!componentState || Boolean(pendingKey)}
                onCheckedChange={(checked) => tickRow(key, checked)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {/* A done task is struck through and muted, so open tasks stand out. */}
                  <label
                    data-fabrik-selection-path={rowPath ? `${rowPath}/label` : undefined}
                    className={`type-body ${
                      value.checked === true
                        ? 'text-muted-foreground line-through'
                        : 'text-card-foreground'
                    }`}
                    htmlFor={checkboxId}
                  >
                    {textOf(value.label)}
                  </label>
                  {late && <Badge variant="destructive">Late</Badge>}
                </div>
                {ownerLine && (
                  <p
                    data-fabrik-selection-path={rowPath ? `${rowPath}/owner` : undefined}
                    className="truncate type-caption text-muted-foreground"
                  >
                    {ownerLine}
                  </p>
                )}
                {byLine && (
                  <p
                    data-fabrik-selection-path={rowPath ? `${rowPath}/checkedBy` : undefined}
                    className="type-caption text-muted-foreground"
                  >
                    {byLine}
                  </p>
                )}
              </div>
              {(dueDateText || late || Boolean(itemActions?.length)) && (
                <div className="flex w-full flex-wrap items-center gap-2 pl-8 sm:contents">
                  {dueDateText && (
                    <span
                      data-fabrik-selection-path={rowPath ? `${rowPath}/dueDate` : undefined}
                      className="shrink-0 type-caption text-muted-foreground tabular-nums"
                    >
                      {dueDateText}
                    </span>
                  )}
                  {itemActions?.length ? (
                    <div className="flex shrink-0 items-center gap-2">
                      {itemActions.map((action, index) => (
                        <ItemActionButton
                          key={`${action.label}-${index}`}
                          action={action}
                          componentId={componentId}
                          recordKey={component.recordKey}
                          recordKeyRefs={getActionSendRecordKeyRefs(
                            actions,
                            action.target.actionId,
                            screenComponents
                          )}
                          rowKey={key}
                          fields={fields}
                          screenId={screenId}
                          inputs={inputs}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </StateBlock>
  );
}
